<!-- Download button + popup panel for the recipe bulk-actions toolbar.
     Owns its open/close state, hint, click-to-nudge behavior, and Escape
     handling. Coordinates with sibling panels via provideBulkActionsPanelGroup
     in the parent toolbar. -->
<template>
  <WrapWithHint class="control" :content="hintText">
    <div @click="onWrapperClick">
      <button
        id="show-download-data-options"
        ref="buttonRef"
        class="button"
        :class="buttonClasses"
        @click="toggle"
      >
        <span class="icon-text">
          <span class="icon">
            <i class="fa fa-download"></i>
          </span>
          <span>Download</span>
        </span>
      </button>
    </div>
  </WrapWithHint>
</template>

<script setup>import { ref as _ref, computed as _computed } from 'vue';

  import { h, ref, watchEffect } from "vue";
  import { onKeyStroke } from "@vueuse/core";

  import BulkDataDownloadPanel from "./bulk-data-download-panel.vue";
  import WrapWithHint from "../wrap-with-hint.vue";
  import { createOptionsPanel } from "../lib/use-bulk-actions.js";

  const props = defineProps({
    selectedIds: { type: Array, required: true },
    count: { type: Number, required: true },
    userId: { type: String, required: true },
    // Pass down from Ruby: `"daily-values-options": daily_values_flat_json`.
    dailyValuesOptions: { type: Array, required: true },
    // Event dispatched on `document` when the user clicks the button with an
    // empty selection. List components listen for this to pulse their
    // checkbox column. Pass null to skip the empty-click nudge.
    hintEventName: { type: String, default: null },
  });

  const buttonRef = _ref(null);
  const openState = ref(false);

  const hintText = _computed(() => {
    if (props.count === 0) return "Select recipes first";
    return null;
  });

  const buttonClasses = _computed(() => {
    if (openState.value) return ["is-link", "is-active"];
    if (props.count > 0) return ["is-info"];
    return ["is-outlined", "click-disabled"];
  });

  watchEffect(() => {
    if (props.count === 0) {
      openState.value = false;
    }
  });

  const panel = createOptionsPanel({
    buttonRef: () => buttonRef.value,
    openState,
    isEnabled: () => props.count > 0,
    buildContent: (keyRef) => ({
      render: () =>
        h("div", { class: "box well" }, [
          h(BulkDataDownloadPanel, {
            "key": keyRef.value,
            "selected-ids": props.selectedIds,
            "user-id": props.userId,
            "daily-values-options": props.dailyValuesOptions,
            "onDone": toggle,
          }),
        ]),
    }),
  });

  function toggle() {
    panel.toggle();
  }

  function onWrapperClick() {
    if (props.count === 0 && props.hintEventName) {
      document.dispatchEvent(new CustomEvent(props.hintEventName));
    }
  }

  onKeyStroke((ev) => {
    if (ev.key === "Escape" && panel.isOpen()) {
      panel.hide();
    }
  });
</script>
